import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "messages-modal__content" }

import {
	computed,
	ref,
	watch
} from "vue"
import { debounce } from "lodash"
import BaseModal from "@/components/common/modal/BaseModal.vue"
import HeaderChatSearchPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatSearchPanel.vue"
import HeaderChatDialogsPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatDialogsPanel.vue"
import HeaderChatMessagesPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatMessagesPanel.vue"
import HeaderChatCreateDialogPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatCreateDialogPanel.vue"
import ChatSearchList from "@/components/main/chat-panel/ChatSearchList.vue"
import { ChatMessage, Dialog } from "@/store/chats"
import { ChatsApi, SearchDialog, SearchMessage } from "@/ api/ChatsApi"
import { headerStore } from "@/store/header"
import { useModalEscape } from "@/composables/useModalEscape"
import { getCurrentLocalDateTime } from "@/utils/auxfunc"


export default /*@__PURE__*/_defineComponent({
  __name: 'HeaderMessagesModal',
  setup(__props, { expose: __expose }) {

const store = headerStore()
const $content = ref<HTMLElement | null>(null)
const $modal = ref<typeof BaseModal | null>(null)
const messagesPanelRef = ref<any>(null)

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

const searchStr = ref('')
const isGlobalSearchStarted = ref(false)
const isMessagesSearchStarted = ref(false)
const suppressSearchWatch = ref(false)

const dialogs = computed(() => store.getDialogs)
const loading = computed(() => store.isDialogsLoading)
const openedChatId = computed(() => store.getOpenedChatId)
const mode = computed(() => store.getChatMode)

const modeComponent = computed(() => ({
	list: HeaderChatDialogsPanel,
	chat: HeaderChatMessagesPanel,
	'search-messages': HeaderChatMessagesPanel,
	'create-chat': HeaderChatCreateDialogPanel,
	'edit-chat': HeaderChatCreateDialogPanel,
	search: HeaderChatDialogsPanel,
} as Record<string, any>))

const focusMessageInput = () => {
	messagesPanelRef.value?.focusSync?.()
}

const onOpenDialog = async (dialog: Dialog | SearchDialog | SearchMessage, isCreated = false) => {
	focusMessageInput()
	suppressSearchWatch.value = true
	isGlobalSearchStarted.value = false
	isMessagesSearchStarted.value = false
	searchStr.value = ''
	if ('ChatId' in dialog) {
		const target = dialogs.value.find(d => d.Id === dialog.ChatId)
		if (target) {
			store.addOpenedChatsHistory(target)
		}
		store.setMode('chat')
		await store.setOpenedChat(dialog.ChatId, dialog.Id)
	} else {
		store.addOpenedChatsHistory({
			Id: dialog.Id,
			Name: dialog.Name,
			ScrollTop: null
		})
		store.setMode('chat')
		await store.setOpenedChat(dialog.Id)
		if (isCreated) {
			try {
				await store.updateDialogInList(getCurrentLocalDateTime())
			} catch (e) {
				console.error(e)
			}
		}
	}
}

const onOpenSearchedMessage = async (message: ChatMessage) => {
	suppressSearchWatch.value = true
	isGlobalSearchStarted.value = false
	isMessagesSearchStarted.value = false
	searchStr.value = ''
	store.setMode('chat')
	await store.setOpenedChat(openedChatId.value, message.Id)
}

const onDeleteDialog = async (dialogId: number) => {
	try {
		await ChatsApi.deleteDialog(dialogId)
		await store.fetchDialogs()
	} catch (e) {
		console.error(e)
	}
}

const onOpenGlobalSearch = () => {
	if (!isGlobalSearchStarted.value && searchStr.value) {
		isGlobalSearchStarted.value = true
	}
}

const onCloseGlobalSearch = () => {
	isGlobalSearchStarted.value = false
}

const onSearchMessagesInChat = async () => {
	if (searchStr.value) {
		if (mode.value !== 'search-messages') {
			store.setMode('search-messages')
		}

		if (!isMessagesSearchStarted.value) {
			isMessagesSearchStarted.value = true
		}

		if (openedChatId.value) {
			await store.fetchSearchedMessages(openedChatId.value, searchStr.value)
		}
	}
}

const onCloseMessagesSearch = () => {
	isMessagesSearchStarted.value = false
	store.setMode('chat')
	store.setOpenedChat(openedChatId.value, null, true)
}

const debouncedGlobalSearch = debounce(onOpenGlobalSearch, 500)
const debouncedMessagesSearch = debounce(onSearchMessagesInChat, 500)

watch(searchStr, (newVal) => {
	if (suppressSearchWatch.value) {
		suppressSearchWatch.value = false
		return
	}
	switch (store.mode) {
		case 'list':
		case 'search':
			if (newVal) {
				debouncedGlobalSearch()
			} else {
				onCloseGlobalSearch()
			}
			return
		case 'chat':
		case 'search-messages':
			if (newVal) {
				debouncedMessagesSearch()
			} else {
				onCloseMessagesSearch()
			}
			return
	}
})

const open = async () => {
	$modal.value?.onOpen()
	activateEsc()
	await store.fetchDialogs()
}

const onClose = () => {
	deactivateEsc()
	store.setResendMessages(null)
}

const onNavigateFromMessage = () => {
	$modal.value?.onClose()
}

__expose({ open })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    ref_key: "$modal",
    ref: $modal,
    "fullscreen-on-mobile": "",
    title: _ctx.$t('messages_label'),
    width: "640px",
    height: "80vh",
    onOnClose: onClose
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "$content",
        ref: $content,
        class: "messages-modal"
      }, [
        (['create-chat', 'edit-chat'].includes(mode.value))
          ? (_openBlock(), _createBlock(HeaderChatCreateDialogPanel, { key: 0 }))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(HeaderChatSearchPanel, {
                modelValue: searchStr.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchStr).value = $event)),
                onRequestFocus: focusMessageInput
              }, null, 8, ["modelValue"]),
              _createElementVNode("div", _hoisted_1, [
                (isGlobalSearchStarted.value)
                  ? (_openBlock(), _createBlock(ChatSearchList, {
                      key: 0,
                      search: searchStr.value,
                      onOnOpenDialog: onOpenDialog,
                      onOnOpenMessage: onOpenDialog
                    }, null, 8, ["search"]))
                  : (isMessagesSearchStarted.value)
                    ? (_openBlock(), _createBlock(HeaderChatMessagesPanel, {
                        key: 1,
                        ref_key: "messagesPanelRef",
                        ref: messagesPanelRef,
                        mode: mode.value,
                        onOnOpenMessage: onOpenSearchedMessage,
                        onOnNavigate: onNavigateFromMessage
                      }, null, 8, ["mode"]))
                    : (_openBlock(), _createBlock(_resolveDynamicComponent(modeComponent.value[mode.value]), {
                        ref_key: "messagesPanelRef",
                        ref: messagesPanelRef,
                        loading: loading.value,
                        dialogs: dialogs.value,
                        mode: mode.value,
                        key: mode.value,
                        onOnOpen: onOpenDialog,
                        onOnDelete: onDeleteDialog,
                        onOnOpenMessage: onOpenSearchedMessage,
                        onOnNavigate: onNavigateFromMessage
                      }, null, 40, ["loading", "dialogs", "mode"]))
              ])
            ], 64))
      ], 512)
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})